import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PlayIcon, CheckIcon, ChevronDownIcon, ChevronUpIcon, ArrowDownIcon, MailIcon, InstagramIcon, CalendarIcon, PlusIcon, MinusIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { CartModal } from '../components/CartModal';
import { TrainingPlanWarningModal } from '../components/TrainingPlanWarningModal';
import { CountdownTimer } from '../components/CountdownTimer';
import { trackAddToCart } from '../utils/analytics';
import { Button } from '../components/Button';
import { ReferralSection } from '../components/ReferralSection';
const BhsExplainer: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="max-w-2xl mx-auto mb-8 md:mb-10" data-id="element-1562">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full group" data-id="element-1563">
        <div className="relative bg-gradient-to-br from-gray-900/90 to-gray-800/90 backdrop-blur-sm rounded-xl border border-gray-700/50 hover:border-green-500/40 transition-all duration-500 overflow-hidden px-5 py-4 md:px-7 md:py-5" data-id="element-1564">
          <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-green-500/30 to-transparent" data-id="element-1565" />
          <div className="flex items-center justify-between" data-id="element-1566">
            <span className="text-sm md:text-lg font-bold text-white tracking-wide" data-id="element-1567">
              Co je <span className="text-green-500" data-id="element-1568">BHS</span> ve zkratce?
            </span>
            <motion.div animate={{
            rotate: isOpen ? 180 : 0
          }} transition={{
            duration: 0.3,
            ease: 'easeInOut'
          }} className="flex-shrink-0 ml-4" data-id="element-1569">
              <ChevronDownIcon className="w-5 h-5 text-green-500" data-id="element-1570" />
            </motion.div>
          </div>
        </div>
      </button>
      <AnimatePresence data-id="element-1571">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.4,
        ease: [0.25, 0.1, 0.25, 1]
      }} className="overflow-hidden" data-id="element-1572">
            <div className="relative bg-gradient-to-br from-gray-900/80 to-gray-800/80 backdrop-blur-sm rounded-b-xl border border-t-0 border-gray-700/50 px-5 py-5 md:px-7 md:py-6 -mt-3 pt-7" data-id="element-1573">
              <div className="space-y-4 text-sm md:text-base text-gray-300 leading-relaxed" data-id="element-1574">
                <p data-id="element-1575">
                  BHS je{' '}
                  <strong className="text-white" data-id="element-1576">BODY HACK SYSTEM</strong>. Je
                  to <strong className="text-white" data-id="element-1577">kompletní systém</strong>,
                  který spojuje trénink i stravu do{' '}
                  <strong className="text-white" data-id="element-1578">
                    jednoho jasného postupu
                  </strong>
                  .
                </p>
                <p data-id="element-1579">
                  Ukáže ti přesně,{' '}
                  <strong className="text-white" data-id="element-1580">jak cvičit</strong>,{' '}
                  <strong className="text-white" data-id="element-1581">jak jíst</strong> a{' '}
                  <strong className="text-white" data-id="element-1582">
                    jak růst co nejrychleji
                  </strong>
                  , aniž bys <strong className="text-white" data-id="element-1583">ztrácel čas</strong>{' '}
                  <strong className="text-white" data-id="element-1584">metodou pokus–omyl</strong>. ⚡
                </p>
                <p data-id="element-1585">
                  Je to tvůj{' '}
                  <strong className="text-green-400" data-id="element-1586">
                    fast track k vysněné postavě
                  </strong>{' '}
                  — systém, který tě{' '}
                  <strong className="text-white" data-id="element-1587">krok za krokem</strong> dovede{' '}
                  <strong className="text-white" data-id="element-1588">k výsledkům</strong> a pomůže
                  ti{' '}
                  <strong className="text-white" data-id="element-1589">
                    využít tvůj potenciál na maximum
                  </strong>
                  . 🚀
                </p>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
};
export const BhsPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    addItem,
    cartModalOpen,
    setCartModalOpen,
    lastAddedItem,
    trainingPlanWarningOpen,
    setTrainingPlanWarningOpen,
    existingTrainingPlanName
  } = useCart();
  const [selectedDuration, setSelectedDuration] = useState<'1' | '3'>('3');
  const [expandedPlan, setExpandedPlan] = useState(false);
  const [openFaqItem, setOpenFaqItem] = useState<number | null>(null);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const handleAddToCart = () => {
    const price = selectedDuration === '1' ? 899 : 1799;
    const itemId = `training-beginner-${selectedDuration}`;
    const itemName = `Tréninkový systém BHS (${selectedDuration === '1' ? '1 měsíc' : '3 měsíce'})`;
    trackAddToCart({
      id: itemId,
      name: itemName,
      price: price,
      category: 'training_plan'
    });
    addItem({
      id: itemId,
      name: itemName,
      price,
      type: 'training',
      details: 'Jednorázová platba'
    });
    navigate('/cart2');
  };
  const handleContinueShopping = () => {
    setCartModalOpen(false);
  };
  const handleGoToCart = () => {
    setCartModalOpen(false);
    navigate('/cart2');
  };
  const scrollToPricing = () => {
    const pricingSection = document.getElementById('pricing-section');
    if (pricingSection) {
      pricingSection.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  const bulletPoints = ['Videoukázky ke každému cviku (správná technika + tempo)', 'Split: 5 tréninků / 2 dny odpočinku', 'Přesně dané série, opakování a pauzy', 'Systém zapisování a sledování progresu', 'Jasně strukturovaný progresivní overload', 'Využití začátečnického potenciálu pro rychlé výsledky', 'Správná délka pauz mezi sériemi pro maximální růst', 'Plán postavený na základě desítek studií + letech zkušeností', 'Plán, který ti dá know-how na to jak správně cvičit'];
  const fullDescription = '🚀 Celý plán je navržený tak, aby si co nejlépe využil tzv. začátečníký potenciál – tedy období, kdy tělo roste nejrychleji a výsledky jdou vidět téměř okamžitě. 📈\n\n🛡️ Zároveň ale dbáme na to, abys ses nepřetrénoval/a – svaly potřebují čas na regeneraci. A právě regeneraci plán zohledňuje, tak aby sis mohl/a budovat formu bezpečně a efektivně. 💪\n\n🤝 Nemusíš se ničeho bát – všechno ti ukážeme krok po kroku. Stačí nabrat odvahu a konečně se rozhodnout se změnit jak fyzicky, tak psychicky. 🧠✨\n\n🔑 Možná právě v tomhle tréninkovém plánu, se skrývá klíč k sebevědomí a změně sebe, po které toužíš. Stačí začít! 🔥';
  const bhsFaqItems = [{
    question: 'Co je BHS – Body Hack System?',
    answer: 'BHS je kompletní systém v aplikaci, který ti krok za krokem ukáže, co přesně cvičit, jak cvičit a jak jíst, aby tvoje tělo začalo růst co nejrychleji.\n\nNemusíš nad ničím přemýšlet — jen následuješ jasný plán.'
  }, {
    question: 'Je tréninkový plán opravdu jednorázová platba?',
    answer: 'Ano. Zaplatíš pouze jednou a získáš přístup k systému na 1 nebo 3 měsíce.\n\nBěhem té doby získáš kompletní know-how, díky kterému už nebudeš muset nikdy platit za další plány nebo trenéry.'
  }, {
    question: 'Na jak dlouho budu mít přístup k plánu?',
    answer: 'Přístup máš na 1 nebo 3 měsíce (dle varianty, kterou si vybereš).\n\n3 měsíce jsou ideální, protože je to dostatečně dlouhá doba na to, aby ses naučil vše potřebné a dokázal se posouvat dál i sám.'
  }, {
    question: 'Jak požádat o vrácení peněz?',
    answer: 'Napište mi na IG zde:',
    hasLink: true,
    linkUrl: 'https://www.instagram.com/ondra.janikk/',
    linkText: '@ondra.janikk',
    extraText: '\n\na odpovězte mi přesně na tyhle otázky, klidně i dopište váš názor, kromě otázek, čím upřímnější názor tím lepší.\n\n1. Bylo pro tebe všechno dostatečně srozumitelné? Pokud ne, co konkrétně ne?\n\n2. Měl jsi pocit, že víš přesně, co máš dělat krok za krokem?\n\n3. Co tě nejvíc zklamalo? (upřímně)\n\n4. Co jsi očekával, že ti to dá — a nedostalo se ti toho?'
  }, {
    question: 'Budu muset po skončení platit znovu?',
    answer: 'Ne. Žádné opakované platby tě nečekají.\n\nZaplatíš jednou a to, co se naučíš, ti zůstane navždy.'
  }, {
    question: 'Jak probíhají check-up cally?',
    answer: 'Každý týden máš možnost 1:1 online check-up callu.\n\nDostaneš přístup k rezervačnímu odkazu, kde si jednoduše vybereš termín.\n\nPokud si nebudeš jistý, jestli něco děláš správně, můžeš to se mnou projít a upravit.'
  }, {
    question: 'Je BHS vhodný i pro úplné začátečníky?',
    answer: 'Ano — hlavně pro začátečníky.\n\nSystém je vytvořený tak, aby tě provedl od úplného začátku a odstranil chaos, který většina lidí na začátku má.'
  }, {
    question: 'Co když jsem už zkoušel jiné tréninkové plány?',
    answer: 'To je naprosto v pořádku.\n\nRozdíl je v tom, že BHS není jen plán, ale ucelený systém, který ti vysvětlí, proč věci fungují — takže konečně pochopíš, co děláš.'
  }, {
    question: 'Co všechno v BHS dostanu?',
    answer: 'Získáš:\n\n• přesně rozepsané tréninky\n• videoukázky ke cvikům\n• systém progresu\n• jasně dané série, opakování a pauzy\n• návod, jak správně jíst\n• a celkový systém, který ti dává smysl od začátku do konce'
  }, {
    question: 'Kdy uvidím první výsledky?',
    answer: 'Pokud začneš systém správně aplikovat, první změny můžeš začít vidět už během několika týdnů.\n\nNejde ale jen o rychlost — jde o to, že konečně víš, co děláš.'
  }];
  return <div className="min-h-screen bg-[#0a0a0a] text-white font-sans selection:bg-green-500/30" data-id="element-1590">
      <CartModal isOpen={cartModalOpen} onClose={handleContinueShopping} onGoToCart={handleGoToCart} onContinueShopping={handleContinueShopping} productName={lastAddedItem || ''} data-id="element-1591" />

      <TrainingPlanWarningModal isOpen={trainingPlanWarningOpen} onClose={() => setTrainingPlanWarningOpen(false)} existingPlanName={existingTrainingPlanName || ''} data-id="element-1592" />

      {/* Header */}
      <header className="py-6 text-center border-b border-gray-800/50" data-id="element-1593"></header>

      <main className="max-w-2xl mx-auto px-4 py-8 md:py-12" data-id="element-1594">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} data-id="element-1595">
          {/* Top Image - Chceš stejné výsledky? */}
          <div className="relative rounded-2xl overflow-hidden mb-6 shadow-2xl shadow-black/50 ring-1 ring-white/10" data-id="element-1596">
            <img src="https://cdn.magicpatterns.com/uploads/jgZdjsB2yKGafZqGnbNtn2/openart-image_CUYi3l9r_1773680450002_raw.jpg" alt="Chceš stejné výsledky?" className="w-full h-auto object-cover" data-id="element-1597" />
          </div>

          {/* Hero Image */}
          <div className="relative rounded-2xl overflow-hidden mb-8 shadow-2xl shadow-black/50 ring-1 ring-white/10" data-id="element-1598"></div>

          {/* Headline */}
          <div className="space-y-6 mb-10" data-id="element-1599">
            <h1 className="text-2xl md:text-3xl font-bold leading-tight text-white" data-id="element-1600">
              Tohle je poslední tréninkový systém, který budeš ve svém{' '}
              <span style={{
              fontFamily: "'Playfair Display', serif"
            }} className="text-green-500 italic" data-id="element-1601">
                životě potřebovat
              </span>
              , abys konečně začal vidět výsledky, po kterých toužíš. 🚀
            </h1>
          </div>

          {/* Video Intro Text */}
          <p className="text-center text-base md:text-lg text-gray-400 mb-6 tracking-wide" data-id="element-1602">
            Pusť si video a zjisti více o{' '}
            <span className="text-green-400 font-semibold" data-id="element-1603">BHS</span> :
          </p>

          {/* Video */}
          <div className="mb-12 relative w-full aspect-video rounded-2xl overflow-hidden shadow-xl border border-gray-800" data-id="element-1604">
            <iframe src="https://www.veed.io/embed/3e707a09-8695-4352-b67e-ea13a36c449f?watermark=0&color=default&sharing=1&title=0" className="absolute inset-0 w-full h-full" frameBorder="0" title="BHS Video" allow="autoplay; fullscreen; picture-in-picture" allowFullScreen data-id="element-1605" />
          </div>

          {/* Sales Copy */}
          <div className="space-y-6 mb-12 text-base md:text-lg text-gray-300 leading-relaxed" data-id="element-1606">
            <p data-id="element-1607">
              Možná už nějakou dobu přemýšlíš, že začneš cvičit. Možná jsi
              dokonce už párkrát chtěl začít, ale nakonec jsi to odložil. Ne
              proto, že bys nechtěl{' '}
              <strong className="text-white" data-id="element-1608">změnit své tělo</strong>… ale
              protože vlastně vůbec{' '}
              <strong className="text-white" data-id="element-1609">nevíš, kde začít</strong>. 🤔
            </p>
            <p data-id="element-1610">
              Možná máš pocit, že{' '}
              <strong className="text-white" data-id="element-1611">nevíš, jaké cviky dělat</strong>,{' '}
              <strong className="text-white" data-id="element-1612">
                jak správně cvičit techniku
              </strong>{' '}
              nebo jestli vůbec{' '}
              <strong className="text-white" data-id="element-1613">děláš věci správně</strong>. A
              někde vzadu v hlavě máš i ten{' '}
              <strong className="text-white" data-id="element-1614">nepříjemný pocit</strong>, že když
              přijdeš do posilovny, budeš vypadat jako někdo, kdo{' '}
              <strong className="text-white" data-id="element-1615">vůbec neví, co dělá</strong>.
            </p>
            <p data-id="element-1616">
              A víš co?{' '}
              <strong className="text-white" data-id="element-1617">
                Tenhle pocit má na začátku skoro každý
              </strong>
              . 🙌
            </p>
            <p data-id="element-1618">
              Právě proto vznikl{' '}
              <strong className="text-white" data-id="element-1619">BHS – Body Hack System</strong>,
              aby tě zbavil všech těchto obav a problémů! 💪
            </p>
            <p data-id="element-1620">
              Je to <strong className="text-white" data-id="element-1621">kompletní systém</strong>,
              který ti <strong className="text-white" data-id="element-1622">krok za krokem</strong>{' '}
              ukáže, <strong className="text-white" data-id="element-1623">jak přesně cvičit</strong>,
              jaké cviky dělat, kolik opakování provádět a jak si nastavit
              tréninky tak, aby tvoje tělo{' '}
              <strong className="text-white" data-id="element-1624">konečně začalo růst</strong>.{' '}
              <strong className="text-white" data-id="element-1625">Nemusíš přemýšlet</strong> nad tím,
              jestli něco děláš špatně nebo jestli bys měl něco změnit.{' '}
              <strong className="text-white" data-id="element-1626">Stačí přijít do posilovny</strong>{' '}
              a <strong className="text-white" data-id="element-1627">následovat systém</strong>, který
              už{' '}
              <strong className="text-white" data-id="element-1628">pomohl mně i mým klientům</strong>{' '}
              dosáhnout{' '}
              <strong className="text-white" data-id="element-1629">skutečných výsledků</strong> — a
              teď může pomoci i tobě docílit tvé vysněné postavy! 🔥
            </p>
          </div>

          {/* Animated Arrow & CTA */}
          <div className="flex flex-col items-center mb-16" data-id="element-1630">
            <motion.div animate={{
            y: [0, 10, 0]
          }} transition={{
            duration: 1.5,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="mb-4" data-id="element-1631">
              <ArrowDownIcon className="w-8 h-8 text-green-500" data-id="element-1632" />
            </motion.div>
            <button onClick={scrollToPricing} className="w-full bg-green-500 hover:bg-green-400 text-black font-black text-xl py-5 rounded-xl transition-all duration-300 shadow-[0_0_20px_rgba(34,197,94,0.3)] hover:shadow-[0_0_30px_rgba(34,197,94,0.5)] active:scale-[0.98]" data-id="element-1633">
              CENÍK BHS!
            </button>
          </div>

          {/* Pricing Section */}
          <div id="pricing-section" className="pt-8" data-id="element-1634">
            <CountdownTimer data-id="element-1635" />

            <motion.div className="premium-card flex flex-col h-full bg-gray-900/80 border border-gray-800 rounded-2xl overflow-hidden mt-6" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5
          }} viewport={{
            once: true
          }} whileHover={{
            y: -5,
            boxShadow: '0 20px 40px -5px rgba(34, 197, 94, 0.3)',
            borderColor: 'rgba(34, 197, 94, 0.3)'
          }} data-id="element-1636">
              <div className="p-3 md:p-6 border-b border-gray-800 relative" data-id="element-1637">
                <div className="flex items-center justify-between" data-id="element-1638">
                  <h3 className="text-base md:text-xl font-black text-green-500 flex items-center" data-id="element-1639">
                    BHS SYSTÉM
                  </h3>
                  <AnimatePresence data-id="element-1640">
                    {selectedDuration === '3' && <motion.div initial={{
                    opacity: 0,
                    scale: 0.8
                  }} animate={{
                    opacity: 1,
                    scale: 1
                  }} exit={{
                    opacity: 0,
                    scale: 0.8
                  }} transition={{
                    duration: 0.2
                  }} className="relative ml-3" data-id="element-1641">
                        <div className="absolute inset-0 bg-gradient-to-r from-green-600 to-green-500 rounded-full blur-sm opacity-60 animate-pulse" data-id="element-1642"></div>
                        <div className="relative bg-gradient-to-r from-green-600 to-green-500 text-white text-[9px] md:text-[10px] font-black px-2.5 md:px-3 py-1 md:py-1 rounded-full shadow-lg border border-white/30" data-id="element-1643">
                          <span className="relative z-10 tracking-wider whitespace-nowrap" data-id="element-1644">
                            NEJPRODÁVANĚJŠÍ
                          </span>
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>
              </div>

              <div className="p-3 md:p-6 flex-1 flex flex-col" data-id="element-1645">
                <div className="flex-1 flex flex-col" data-id="element-1646">
                  <ul className="space-y-1.5 md:space-y-3 mb-3 md:mb-6 flex-1" data-id="element-1647">
                    {bulletPoints.map((point, idx) => <li key={idx} className="flex items-start gap-2 md:gap-4" data-id="element-1648">
                        <div className="flex-shrink-0 mt-0.5" data-id="element-1649">
                          <CheckIcon className="w-3.5 h-3.5 md:w-5 md:h-5 text-green-500" strokeWidth={2.5} data-id="element-1650" />
                        </div>
                        <span className="text-gray-200 text-xs md:text-base font-medium leading-snug md:leading-relaxed" data-id="element-1651">
                          {point}
                        </span>
                      </li>)}
                  </ul>

                  <motion.button onClick={() => setExpandedPlan(!expandedPlan)} className="flex items-center text-green-500 hover:text-green-400 transition-colors text-xs md:text-sm font-bold mb-3 md:mb-6" whileHover={{
                  x: 5
                }} transition={{
                  duration: 0.2
                }} data-id="element-1652">
                    {expandedPlan ? <>
                        <span data-id="element-1653">MÉNĚ INFORMACÍ</span>
                        <ChevronUpIcon className="w-4 h-4 ml-1" data-id="element-1654" />
                      </> : <>
                        <span data-id="element-1655">VÍCE INFORMACÍ</span>
                        <ChevronDownIcon className="w-4 h-4 ml-1" data-id="element-1656" />
                      </>}
                  </motion.button>

                  <motion.div initial={false} animate={{
                  height: expandedPlan ? 'auto' : 0,
                  opacity: expandedPlan ? 1 : 0
                }} transition={{
                  duration: 0.3
                }} className="overflow-hidden" data-id="element-1657">
                    {expandedPlan && <div className="mt-1 text-gray-300 bg-gray-800/50 p-4 rounded-xl text-sm md:text-base mb-5 leading-relaxed border border-gray-700/50" data-id="element-1658">
                        <p className="whitespace-pre-line" data-id="element-1659">{fullDescription}</p>
                      </div>}
                  </motion.div>

                  <div className="flex justify-center mb-3 md:mb-6 bg-gray-800/80 rounded-lg md:rounded-xl overflow-hidden p-1 md:p-1.5 border border-gray-700/50" data-id="element-1660">
                    <button onClick={() => setSelectedDuration('1')} className={`flex-1 py-2 md:py-3 text-center font-bold transition-all duration-300 text-xs md:text-base rounded-md md:rounded-lg ${selectedDuration === '1' ? 'bg-gray-700 text-white shadow-md' : 'bg-transparent text-gray-400 hover:text-gray-200'}`} data-id="element-1661">
                      1 měsíc
                    </button>
                    <button onClick={() => setSelectedDuration('3')} className={`flex-1 py-2 md:py-3 text-center font-bold transition-all duration-300 text-xs md:text-base rounded-md md:rounded-lg ${selectedDuration === '3' ? 'bg-green-600 text-white shadow-md shadow-green-900/20' : 'bg-transparent text-gray-400 hover:text-gray-200'}`} data-id="element-1662">
                      3 měsíce
                    </button>
                  </div>

                  <div className="text-center mb-3 md:mb-8" data-id="element-1663">
                    <div className="text-2xl md:text-5xl font-black text-green-500 mb-1.5 md:mb-3 drop-shadow-[0_0_10px_rgba(34,197,94,0.3)]" data-id="element-1664">
                      {selectedDuration === '1' ? '899 Kč' : '1799 Kč'}
                    </div>

                    <div className="relative inline-block bg-gradient-to-r from-green-900/30 to-emerald-900/30 border border-green-500/20 rounded-lg px-3 md:px-5 py-1 md:py-2 mb-1.5 md:mb-3 backdrop-blur-sm" data-id="element-1665">
                      <div className="text-xs md:text-base text-gray-300 flex items-center justify-center gap-2 md:gap-3" data-id="element-1666">
                        <span className="text-gray-500 line-through decoration-red-500/40 decoration-1" data-id="element-1667">
                          {selectedDuration === '1' ? '1397 Kč' : '2697 Kč'}
                        </span>
                        <span className="font-bold text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-300 uppercase tracking-wide" data-id="element-1668">
                          {selectedDuration === '1' ? 'Ušetříš 498 Kč' : 'Ušetříš 898 Kč'}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center justify-center gap-2 text-xs md:text-base mt-1 md:mt-3" data-id="element-1669">
                      <span className="font-bold text-transparent bg-clip-text bg-gradient-to-r from-green-400 via-emerald-300 to-green-400 tracking-wide drop-shadow-[0_0_8px_rgba(34,197,94,0.4)]" data-id="element-1670">
                        Jednou zaplatíš a už nikdy nebudeš muset platit znovu!
                      </span>
                    </div>
                  </div>

                  <button onClick={handleAddToCart} className="w-full bg-green-600 text-white py-3 md:py-5 rounded-xl md:rounded-2xl font-black text-sm md:text-lg flex items-center justify-center hover:bg-green-500 transition-all duration-300 shadow-[0_0_20px_rgba(34,197,94,0.3)] hover:shadow-[0_0_30px_rgba(34,197,94,0.5)] active:scale-[0.98]" data-id="element-1671">
                    <CheckIcon className="w-4 h-4 md:w-6 md:h-6 mr-2" strokeWidth={3} data-id="element-1672" />
                    PŘIDAT DO KOŠÍKU
                  </button>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Checkmarks + BHS Explainer */}
          <div className="mt-10 md:mt-14 max-w-2xl mx-auto" data-id="element-1673">
            <div className="flex flex-wrap justify-center gap-3 md:gap-5 mb-6 md:mb-8" data-id="element-1674">
              <div className="flex items-center" data-id="element-1675">
                <CheckIcon className="w-4 h-4 md:w-5 md:h-5 text-green-500 mr-2" data-id="element-1676" />
                <span className="text-gray-300 text-xs md:text-base" data-id="element-1677">
                  Jednorázová platba
                </span>
              </div>
              <div className="flex items-center" data-id="element-1678">
                <CheckIcon className="w-4 h-4 md:w-5 md:h-5 text-green-500 mr-2" data-id="element-1679" />
                <span className="text-gray-300 text-xs md:text-base" data-id="element-1680">
                  Získáš okamžitý přístup do emailu
                </span>
              </div>
            </div>

            {/* BHS Explainer */}
            <BhsExplainer data-id="element-1681" />
          </div>

          {/* Money-Back Guarantee Section */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7
        }} viewport={{
          once: true
        }} className="mt-12 md:mt-16 md:max-w-md md:mx-auto" data-id="element-1682">
            <div className="relative bg-gradient-to-br from-gray-900 via-[#0d1a0f] to-gray-900 rounded-2xl p-8 md:p-10 border border-green-500/30 shadow-[0_0_60px_-10px_rgba(34,197,94,0.15)] overflow-hidden" data-id="element-1683">
              {/* Decorative glows */}
              <div className="absolute -right-20 -top-20 w-64 h-64 bg-green-500/5 rounded-full filter blur-3xl" data-id="element-1684"></div>
              <div className="absolute -left-20 -bottom-20 w-64 h-64 bg-green-500/5 rounded-full filter blur-3xl" data-id="element-1685"></div>
              <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-green-500/50 to-transparent" data-id="element-1686"></div>
              <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-green-500/20 to-transparent" data-id="element-1687"></div>

              {/* Badge Image */}
              <div className="flex justify-center mb-6" data-id="element-1688">
                <motion.div initial={{
                scale: 0.8,
                rotate: -5
              }} whileInView={{
                scale: 1,
                rotate: 0
              }} transition={{
                duration: 0.6,
                ease: 'easeOut'
              }} viewport={{
                once: true
              }} data-id="element-1689">
                  <img src="https://cdn.magicpatterns.com/uploads/xwBpFN12dGe77mqcwAqpfb/551bf2d4-926f-41a3-87bd-3b63ea8f702c.png" alt="5 Dní Garance Vrácení Peněz" className="w-28 h-28 md:w-36 md:h-36 object-contain drop-shadow-[0_0_20px_rgba(34,197,94,0.3)]" data-id="element-1690" />
                </motion.div>
              </div>

              {/* Heading */}
              <h3 className="text-2xl md:text-3xl font-black text-center text-white mb-6 tracking-tight" data-id="element-1691">
                100% <span className="text-green-500" data-id="element-1692">GARANCE</span> VRÁCENÍ
                PENĚZ
              </h3>

              {/* Guarantee Text */}
              <div className="space-y-4 text-center" data-id="element-1693">
                <p className="text-base md:text-lg text-gray-200 leading-relaxed font-medium" data-id="element-1694">
                  Pokud do 5 dní po zakoupení nebudete mít pocit, že se
                  investice vyplatila, vrátím ti{' '}
                  <span className="text-green-400 font-bold" data-id="element-1695">
                    každou korunu
                  </span>
                  .
                </p>
                <p className="text-base md:text-lg text-gray-300 leading-relaxed" data-id="element-1696">
                  Žádné složité formuláře. Žádné zdůvodňování.
                </p>
                <p className="text-lg md:text-xl font-bold text-white italic" style={{
                fontFamily: "'Playfair Display', serif"
              }} data-id="element-1697">
                  Celý risk je na mě. Tak moc věřím v kvalitu mého produktu.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Referral System Section */}
          <ReferralSection data-id="element-1698" />

          {/* BHS FAQ Section */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} viewport={{
          once: true
        }} className="mt-16 md:mt-20 md:max-w-2xl md:mx-auto" data-id="element-1699">
            <div className="relative" data-id="element-1700">
              <h2 className="text-3xl md:text-4xl font-bold mb-10 text-center" data-id="element-1701">
                <motion.span initial={{
                opacity: 0
              }} whileInView={{
                opacity: 1
              }} transition={{
                duration: 1.2,
                ease: 'easeOut'
              }} viewport={{
                once: true
              }} className="text-transparent bg-clip-text bg-gradient-to-r from-white via-gray-200 to-white" data-id="element-1702">
                  FAQ
                </motion.span>
              </h2>

              <div className="space-y-4" data-id="element-1703">
                {bhsFaqItems.map((item, index) => <motion.div key={index} initial={{
                opacity: 0,
                y: 20
              }} whileInView={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.4,
                delay: index * 0.1
              }} viewport={{
                once: true
              }} className="bg-gray-900/60 backdrop-blur-sm rounded-xl border border-green-500/30 overflow-hidden hover:border-green-500/50 transition-all duration-300" data-id="element-1704">
                    <button onClick={() => setOpenFaqItem(openFaqItem === index ? null : index)} className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-white/5 transition-colors duration-300" data-id="element-1705">
                      <span className="text-base md:text-lg font-medium text-white pr-4" data-id="element-1706">
                        {item.question}
                      </span>
                      <div className="flex-shrink-0" data-id="element-1707">
                        {openFaqItem === index ? <MinusIcon className="w-6 h-6 text-green-500" data-id="element-1708" /> : <PlusIcon className="w-6 h-6 text-green-500" data-id="element-1709" />}
                      </div>
                    </button>

                    <AnimatePresence data-id="element-1710">
                      {openFaqItem === index && <motion.div initial={{
                    height: 0,
                    opacity: 0
                  }} animate={{
                    height: 'auto',
                    opacity: 1
                  }} exit={{
                    height: 0,
                    opacity: 0
                  }} transition={{
                    duration: 0.3,
                    ease: 'easeInOut'
                  }} className="overflow-hidden" data-id="element-1711">
                          <div className="px-6 pb-5 pt-1" data-id="element-1712">
                            <div className="text-sm md:text-base text-gray-300 leading-relaxed whitespace-pre-line" data-id="element-1713">
                              {item.answer}
                              {(item as any).hasLink && <>
                                  {' '}
                                  <a href={(item as any).linkUrl} target="_blank" rel="noopener noreferrer" className="text-green-400 font-semibold underline underline-offset-2 decoration-green-500/50 hover:decoration-green-400 transition-colors" data-id="element-1714">
                                    {(item as any).linkText}
                                  </a>
                                  {(item as any).extraText && <span className="whitespace-pre-line" data-id="element-1715">
                                      {(item as any).extraText}
                                    </span>}
                                </>}
                            </div>
                          </div>
                        </motion.div>}
                    </AnimatePresence>
                  </motion.div>)}
              </div>
            </div>
          </motion.div>

          {/* Contact Section */}
          <motion.div className="mt-20 mb-12" initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} viewport={{
          once: true
        }} data-id="element-1716">
            <div className="text-center mb-8" data-id="element-1717">
              <h2 className="text-xl md:text-2xl font-black tracking-tight leading-relaxed" data-id="element-1718">
                MÁŠ NĚJAKÉ <span className="text-green-500" data-id="element-1719">OTÁZKY</span>?{' '}
                <span className="text-green-500" data-id="element-1720">NAPIŠ MI</span>, NEBO SI{' '}
                <span className="text-green-500" data-id="element-1721">ZAREZERVUJ</span> SVŮJ ONLINE
                MEETING <span className="text-green-500" data-id="element-1722">ZDARMA</span>!
              </h2>
              <div className="w-16 h-0.5 bg-gradient-to-r from-green-500/0 via-green-500 to-green-500/0 mx-auto mt-4" data-id="element-1723"></div>
            </div>

            <div className="space-y-4" data-id="element-1724">
              {/* Meeting CTA - First */}
              <motion.a href="https://calendly.com/ondrej-janik-body-hack/15-minutovy-meeting-otazky" target="_blank" rel="noopener noreferrer" initial={{
              opacity: 0,
              y: 15
            }} whileInView={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              delay: 0.1
            }} viewport={{
              once: true
            }} whileHover={{
              y: -4,
              boxShadow: '0 16px 40px -8px rgba(34, 197, 94, 0.3)'
            }} className="group relative block bg-gradient-to-br from-gray-900/95 via-gray-800/95 to-gray-900/95 backdrop-blur-xl p-7 rounded-2xl border border-green-500/20 hover:border-green-500/50 transition-all duration-500 overflow-hidden shadow-lg shadow-black/20" data-id="element-1725">
                <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-green-500/40 to-transparent" data-id="element-1726"></div>
                <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-green-500/10 to-transparent" data-id="element-1727"></div>
                <div className="absolute -right-16 -top-16 w-32 h-32 bg-green-500/5 rounded-full filter blur-2xl group-hover:bg-green-500/10 transition-all duration-500" data-id="element-1728"></div>
                <div className="flex flex-col items-center text-center" data-id="element-1729">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-500/15 to-green-500/5 rounded-full flex items-center justify-center mb-3.5 group-hover:from-green-500/25 group-hover:to-green-500/10 transition-all duration-500 ring-1 ring-green-500/20 group-hover:ring-green-500/40" data-id="element-1730">
                    <CalendarIcon className="w-5 h-5 text-green-500" data-id="element-1731" />
                  </div>
                  <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500 mb-2" data-id="element-1732">
                    Online Meeting
                  </span>
                  <span className="text-green-400 text-sm font-bold tracking-wide" data-id="element-1733">
                    ZAREZERVUJ SI MEETING ZDARMA!
                  </span>
                </div>
              </motion.a>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1734">
                {/* Email */}
                <motion.a href="mailto:ondrej.janik@body-hack.cz" initial={{
                opacity: 0,
                y: 15
              }} whileInView={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.5,
                delay: 0.2
              }} viewport={{
                once: true
              }} whileHover={{
                y: -4,
                boxShadow: '0 16px 40px -8px rgba(34, 197, 94, 0.3)'
              }} className="group relative bg-gradient-to-br from-gray-900/95 via-gray-800/95 to-gray-900/95 backdrop-blur-xl p-7 rounded-2xl border border-green-500/20 hover:border-green-500/50 transition-all duration-500 overflow-hidden shadow-lg shadow-black/20" data-id="element-1735">
                  <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-green-500/40 to-transparent" data-id="element-1736"></div>
                  <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-green-500/10 to-transparent" data-id="element-1737"></div>
                  <div className="absolute -right-16 -top-16 w-32 h-32 bg-green-500/5 rounded-full filter blur-2xl group-hover:bg-green-500/10 transition-all duration-500" data-id="element-1738"></div>
                  <div className="flex flex-col items-center text-center" data-id="element-1739">
                    <div className="w-12 h-12 bg-gradient-to-br from-green-500/15 to-green-500/5 rounded-full flex items-center justify-center mb-3.5 group-hover:from-green-500/25 group-hover:to-green-500/10 transition-all duration-500 ring-1 ring-green-500/20 group-hover:ring-green-500/40" data-id="element-1740">
                      <MailIcon className="w-5 h-5 text-green-500" data-id="element-1741" />
                    </div>
                    <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500 mb-2" data-id="element-1742">
                      Email
                    </span>
                    <span className="text-green-400 text-sm font-bold break-all" data-id="element-1743">
                      ondrej.janik@body-hack.cz
                    </span>
                  </div>
                </motion.a>

                {/* Instagram */}
                <motion.a href="https://www.instagram.com/ondra.janikk/" target="_blank" rel="noopener noreferrer" initial={{
                opacity: 0,
                y: 15
              }} whileInView={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.5,
                delay: 0.3
              }} viewport={{
                once: true
              }} whileHover={{
                y: -4,
                boxShadow: '0 16px 40px -8px rgba(34, 197, 94, 0.3)'
              }} className="group relative bg-gradient-to-br from-gray-900/95 via-gray-800/95 to-gray-900/95 backdrop-blur-xl p-7 rounded-2xl border border-green-500/20 hover:border-green-500/50 transition-all duration-500 overflow-hidden shadow-lg shadow-black/20" data-id="element-1744">
                  <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-green-500/40 to-transparent" data-id="element-1745"></div>
                  <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-green-500/10 to-transparent" data-id="element-1746"></div>
                  <div className="absolute -right-16 -top-16 w-32 h-32 bg-green-500/5 rounded-full filter blur-2xl group-hover:bg-green-500/10 transition-all duration-500" data-id="element-1747"></div>
                  <div className="flex flex-col items-center text-center" data-id="element-1748">
                    <div className="w-12 h-12 bg-gradient-to-br from-green-500/15 to-green-500/5 rounded-full flex items-center justify-center mb-3.5 group-hover:from-green-500/25 group-hover:to-green-500/10 transition-all duration-500 ring-1 ring-green-500/20 group-hover:ring-green-500/40" data-id="element-1749">
                      <InstagramIcon className="w-5 h-5 text-green-500" data-id="element-1750" />
                    </div>
                    <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500 mb-2" data-id="element-1751">
                      Instagram
                    </span>
                    <span className="text-green-400 text-sm font-bold" data-id="element-1752">
                      @ondra.janikk
                    </span>
                  </div>
                </motion.a>
              </div>
            </div>
          </motion.div>

          {/* Footer Link */}
          <div className="text-center pb-8 mt-8" data-id="element-1753">
            <a href="#" className="text-sm text-gray-500 hover:text-gray-300 underline transition-colors" data-id="element-1754">
              Privacy Policy
            </a>
          </div>
        </motion.div>
      </main>
    </div>;
};